import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { EducationItem, Enrollment } from "../../types/types";
import { API_URL } from "../../config";

export const educationApi = createApi({
  reducerPath: "education",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["EducationItem", "Enrollment"],
  endpoints: (builder) => ({
    getEducationItems: builder.query<EducationItem[], void>({
      query: () => "education-item",
      providesTags: ["EducationItem"],
    }),
    getEducationItemBySlug: builder.query<EducationItem, string>({
      query: (slug) => `education-item/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "EducationItem", id: slug }],
    }),
    // Phase 3 (EB-482): admin-only CRUD for the education-item maintenance page. The backend keys
    // items by `slug` and gates POST/PUT/DELETE on the `admin` role (see lambda/education).
    createEducationItem: builder.mutation<EducationItem, EducationItem>({
      query: (body) => ({ url: "education-item", method: "POST", body }),
      invalidatesTags: ["EducationItem"],
    }),
    updateEducationItem: builder.mutation<EducationItem, { slug: string; patch: EducationItem }>({
      query: ({ slug, patch }) => ({ url: `education-item/${slug}`, method: "PUT", body: patch }),
      invalidatesTags: (_result, _error, { slug }) => [
        { type: "EducationItem", id: slug },
        "EducationItem",
      ],
    }),
    deleteEducationItem: builder.mutation<{ slug: string }, string>({
      query: (slug) => ({ url: `education-item/${slug}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, slug) => [
        { type: "EducationItem", id: slug },
        "EducationItem",
      ],
    }),
    // Phase 3 (EB-482): scoped to the caller server-side via the Auth0 `sub` (no `?user=` param).
    getEnrollments: builder.query<Enrollment[], void>({
      query: () => "enrollment",
      providesTags: ["Enrollment"],
    }),
    createEnrollment: builder.mutation<Enrollment, { education_item: number }>({
      query: (body) => ({
        url: "enrollment",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Enrollment"],
    }),
  }),
});

export const {
  useGetEducationItemsQuery,
  useGetEducationItemBySlugQuery,
  useGetEnrollmentsQuery,
  useCreateEnrollmentMutation,
  useCreateEducationItemMutation,
  useUpdateEducationItemMutation,
  useDeleteEducationItemMutation,
} = educationApi;

export default educationApi;
